/*  iceberg-ui  */
import { WithContext } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Grid, Paper } from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { dateFilter, lastXDays } from 'application/Shared/Components/Cells';
import {
    DataAPIVisualsPieChart,
    DataAPIVisualsTotal
} from 'application/Shared/Components/DataAPIVisualsCard';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2ContextProvider,
    SimpleHeader,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { sortByTimestamp } from 'application/Shared/Components/TableSortFunctions';
import { SentenceCase } from 'application/Shared/Components/Text';
import { TABLENAMES } from 'application/Shared/Constants';
import { formatDistanceToNow } from 'application/Shared/Helpers/Dates';
import { Apple, Windows } from 'application/Shared/Icons';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/* Internal */
import { DeviceTable } from './internal/DeviceTable';
import {
    DeviceTableContext,
    DeviceTableContextProvider
} from './internal/DeviceTableContext';

const columns = [
    {
        accessorKey: 'name',
        cell: ({ row }) => (
            <Box display="flex" alignItems="center">
                {row.original.os === 'MAC' ? <Apple /> : <Windows />}
                <span style={{ marginLeft: '8px' }}>{row.original.name}</span>
            </Box>
        ),
        enableHiding: false,
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'name',
        name: 'Common.name',
        size: 200,
        testId: 'deviceName'
    },
    {
        accessorKey: 'macAddress',
        cell: ({ row }) => row.original.macAddress || '-',
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'macAddress',
        name: 'Common.macAddress',
        size: 175,
        testId: 'deviceMacAddress'
    },
    {
        accessorKey: 'release',
        cell: ({ row }) => row.original.release || '-',
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'deviceRelease',
        maxSize: 100,
        name: 'Common.version',
        size: 100,
        testId: 'deviceRelease'
    },
    {
        accessorKey: 'os',
        cell: ({ row }) => <SentenceCase text={row.original.os} />,
        filterFn: 'arrIncludesSome',
        header: Header,
        id: 'os',
        maxSize: 150,
        name: 'Common.os',
        size: 150,
        testId: 'deviceOS'
    },
    {
        accessorKey: 'dateUpdated',
        cell: ({ row }) => formatDistanceToNow(row.original.dateUpdated),
        filterFn: lastXDays,
        header: Header,
        id: 'dateUpdated',
        meta: { filter: dateFilter },
        name: 'Common.lastUpdated',
        size: 150,
        sortingFn: sortByTimestamp,
        testId: 'deviceDateUpdated'
    },
    {
        accessorKey: 'actions',
        cell: ({ row }) => (
            <WithContext context={DeviceTableContext}>
                {({ setDeletingId, t }) => (
                    <KebabMenu
                        data-testid={`deviceTableKebabMenu${row.original.id}`}
                        onClick={() => setDeletingId(row.original.id)}
                        options={[
                            {
                                key: 'devicesTableDeleteBtn',
                                label: t('Devices.deleteDevice'),
                                value: 'deleteDevice'
                            }
                        ]}
                    />
                )}
            </WithContext>
        ),
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: (
            <SimpleHeader text="Common.actions" justifyContent="flex-end" />
        ),
        id: 'actions',
        maxSize: 50,
        meta: {
            align: 'right',
            header: { justifyContent: 'flex-end' }
        },
        name: 'Common.actions',
        size: 50,
        testId: 'actions'
    }
];

const Charts = () => {
    const { rowData: data, t } = useManagedTableV2Context();

    return (
        <Grid container spacing={3} data-testid="adminUsersPagesDevicesCharts">
            <Grid item xs={12} sm={6} lg={4}>
                <DataAPIVisualsTotal
                    card
                    data={data}
                    title={t('Devices.total')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <DataAPIVisualsPieChart
                    accessorKey="release"
                    card
                    data={data}
                    title={t('Devices.release')}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
                <DataAPIVisualsPieChart
                    accessorKey="os"
                    card
                    data={data}
                    title={t('Devices.operatingSystem')}
                />
            </Grid>
        </Grid>
    );
};

const baseParameters = { excludeExpired: true };

export const AdminUsersDevices = () => {
    const { t } = useTranslation();

    return (
        <Box data-testid="adminUsersPagesDevices">
            <PageTitle title={t('Common.devices')} />
            <DeviceTableContextProvider>
                <ManagedTableV2ContextProvider
                    baseParameters={baseParameters}
                    columns={columns}
                    getRowId="id"
                    ignoreSearchParams
                    rowsApi="/device"
                    mode="client"
                    rowsApiPreload
                    rowsPerPage={10}
                    searchEnabled
                    showAutoupdate
                    sortDirection="ASC"
                    sortKey="name"
                    tableName={TABLENAMES.ADMIN_USER_DEVICES}
                >
                    <Charts />
                    <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{ mt: '24px', p: 0 }}
                    >
                        <DeviceTable />
                    </Paper>
                </ManagedTableV2ContextProvider>
            </DeviceTableContextProvider>
        </Box>
    );
};
